import {
  getBasePayRate,
  getIncomeItem,
  getMemberRecords,
  getTotalByFieldName,
  getUniqueMemberIds,
} from './incomeTotals.utils';
import { INCOME_CHECKLIST_PAY_TYPES } from 'pages/AffordaWagesAndIncome/constants';
import { CHILD_SUPPORT_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.childSupport';

export const getIncomeChildSupports = (records) => {
  if (!records || !records?.length) {
    return {
      childSupports: [],
      totalIncome: 0,
    };
  }

  // Get unique memberIds
  const memberIds = getUniqueMemberIds(records);

  // Process records for each member
  const memberIncomes = memberIds.map((memberId) => {
    const memberRecords = getMemberRecords(records, memberId);

    const incomeItems = [];
    let childSupportValues = {};

    memberRecords.forEach((record) => {
      childSupportValues[record.id] = {
        record: {},
        incomeItem: {},
        incomeChecklist: {},
      };
      // Save these the record to help mapping later
      childSupportValues[record.id].record = { ...record };
      // Even if the applicant has not child support records,
      // we still need to include the anticipated child support
      childSupportValues[record.id].incomeChecklist = {
        anticipateChildSupport:
          record[CHILD_SUPPORT_FIELD_NAMES.anticipateChildSupport],
        anticipateChildSupportCounty: record.anticipateChildSupportCounty,
        anticipateChildSupportState: record.anticipateChildSupportState,
        collectChildSupport:
          record[CHILD_SUPPORT_FIELD_NAMES.reasonableEfforts],
        collectChildSupportCounty: record[CHILD_SUPPORT_FIELD_NAMES.county],
        collectChildSupportState: record[CHILD_SUPPORT_FIELD_NAMES.state],
        courtOrderedChildSupport:
          record[CHILD_SUPPORT_FIELD_NAMES.courtOrderedChildSupport],
        courtOrderedChildSupportPayFrequency:
          record[CHILD_SUPPORT_FIELD_NAMES.payFrequencyOrdered],
        receivingChildSupport:
          record[CHILD_SUPPORT_FIELD_NAMES.receivingOrdered],
        receivingChildSupportPayFrequency:
          record[CHILD_SUPPORT_FIELD_NAMES.payFrequencyReceived],
      };

      // Skip if no court order or not receiving
      if (
        record.courtOrderedChildSupport !== 'yes' &&
        record.receivingOrdered !== 'yes'
      ) {
        return;
      }

      // Determine which frequency and amount to use
      let payRateCourtOrdered, payRateReceiving;

      const hasCourtOrderedChildSupport =
        record.courtOrderedChildSupport === 'yes';
      const isReceivingCourtOrderedChildSupport =
        record.receivingOrdered === 'yes';
      const isReceivingAnyCourtOrderedChildSupport =
        record.receivingAny === 'yes';

      // Assign Court ordered pay
      if (hasCourtOrderedChildSupport) {
        payRateCourtOrdered = getBasePayRate({
          payFrequency: record.payFrequencyOrdered,
          weeklyAmount: record.weeklyAmountOrdered,
          monthlyAmount: record.monthlyAmountOrdered,
          annualAmount: record.annualAmountOrdered,
        });

        childSupportValues[
          record.id
        ].incomeChecklist.courtOrderedChildSupportPayRate = payRateCourtOrdered;
      }

      // Assign Receiving pay
      if (
        isReceivingCourtOrderedChildSupport ||
        isReceivingAnyCourtOrderedChildSupport
      ) {
        payRateReceiving = getBasePayRate({
          payFrequency: record.payFrequencyReceived,
          weeklyAmount: record.weeklyAmountReceived,
          monthlyAmount: record.monthlyAmountReceived,
          annualAmount: record.annualAmountReceived,
        });

        childSupportValues[
          record.id
        ].incomeChecklist.receivingChildSupportPayRate = payRateReceiving;
      }

      // Determine effective payRate, payFrequency
      const payRate = isReceivingCourtOrderedChildSupport
        ? payRateCourtOrdered
        : payRateReceiving;

      // Skip if no amount
      if (!payRate) {
        return;
      }

      const courtOrderedIncomeItem = getIncomeItem({
        id: record.id,
        memberId,
        payType: INCOME_CHECKLIST_PAY_TYPES.childSupport,
        payFrequency: record.payFrequencyOrdered,
        payRate: payRateCourtOrdered,
      });

      const receivingIncomeItem = getIncomeItem({
        id: record.id,
        memberId,
        payType: INCOME_CHECKLIST_PAY_TYPES.childSupport,
        payFrequency: record.payFrequencyReceived,
        payRate: payRateReceiving ?? payRateCourtOrdered,
      });

      childSupportValues[record.id].incomeItem = {
        courtOrderedIncomeItem,
        receivingIncomeItem,
      };

      incomeItems.push(receivingIncomeItem);
    });

    // Calculate member's total income
    const memberTotalIncome = getTotalByFieldName(incomeItems, 'incomeTotal');

    return {
      memberId,
      childSupports: incomeItems,
      childSupportValues,
      totalIncome: memberTotalIncome,
    };
  });

  // Filter out members with no valid income items
  const validMemberIncomes = memberIncomes.filter((member) => {
    const hasChildSupports = member.childSupports.length > 0;
    const allRecordsAreAnticipatedChildSupport = Object.values(
      member.childSupportValues,
    ).every((record) => record.anticipateChildSupport === 'yes');

    return hasChildSupports || allRecordsAreAnticipatedChildSupport;
  });

  // Calculate grand total
  const grandTotal = getTotalByFieldName(validMemberIncomes, 'totalIncome');

  return {
    childSupports: validMemberIncomes,
    totalIncome: grandTotal,
  };
};
